<template>
  <v-app-bar app color="primary" dark height="88">
    <v-icon class="nav-logo" size="48.1">
      $vpLogo
    </v-icon>

    <v-toolbar-items class="grow" v-if="!logoOnly">
      <v-btn
        v-for="item in navBarItems"
        :class="{ dontRemoveItsRequiredForRefresh: isAssumingClientRole }"
        text
        :key="`navbar-item-${item.name}`"
        v-bind="bindLink(item)"
      >
        <v-icon v-if="item.icon" v-bind:style="item.iconStyles">
          {{ item.icon }}
        </v-icon>
        <span class="ml-1">{{ item.name }}</span>
      </v-btn>
      <v-spacer></v-spacer>

      <v-menu offset-y :close-on-content-click="false" v-model="accountOpen">
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            text
            v-bind="attrs"
            v-on="on"
            @mouseenter="accountHover = true"
            @mouseleave="accountHover = false"
          >
            <v-icon v-if="!accountHover && !accountOpen" class="mr-1"
              >$myAccount</v-icon
            >
            <v-icon v-else :class="accountOpen && 'active'"
              >mdi-chevron-down</v-icon
            >
            <span class="ml-1">My Account</span>
          </v-btn>
        </template>
        <v-list>
          <template v-if="isAdminUser">
            <v-list-item>
              <span>I'm a </span>
              <v-spacer />
              <v-btn-toggle dense v-model="assumedRole">
                <v-btn value="client-user" @click="useClientRole">
                  <span>Client User</span>
                </v-btn>
                <v-btn value="vendor-user" @click="useVendorRole">
                  <span>Vendor User</span>
                </v-btn>
              </v-btn-toggle>
            </v-list-item>
            <v-divider></v-divider>
          </template>
          <template v-if="isAdminUser">
            <v-list-item class="my-1">
              <v-autocomplete
                outlined
                dense
                hide-details
                class="selector"
                v-model="selectedClient"
                :items="potentialClients"
                :loading="clientSelectorLoading"
                :search-input.sync="clientSearchText"
                item-text="name"
                item-value="id"
                label="Selected Client"
                placeholder="Start typing to Search"
                return-object
              ></v-autocomplete>
            </v-list-item>
            <v-divider></v-divider>
          </template>
          <template v-if="isAdminUser">
            <v-list-item class="my-1">
              <v-autocomplete
                outlined
                dense
                hide-details
                class="selector"
                v-model="selectedVendor"
                :items="potentialVendors"
                :loading="vendorSelectorLoading"
                :search-input.sync="vendorSearchText"
                item-text="name"
                item-value="id"
                label="Selected Vendor"
                placeholder="Start typing to Search"
                return-object
              ></v-autocomplete>
            </v-list-item>
            <v-divider></v-divider>
          </template>
          <template v-if="!isClientUser">
            <v-list-item to="/account">
              Account
            </v-list-item>
            <v-divider></v-divider>
          </template>
          <v-list-item
            :href="`/csarf/logoutPage`"
            @click.native="logEvent('Clicked button to log out')"
          >
            <v-list-item-title>Logout</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
    </v-toolbar-items>
  </v-app-bar>
</template>

<script>
import { getNavBarItems } from "@/router/routes";
import hasCurrentUser from "@/mixins/has-current-user";
import { get, put } from "@/services/request";
import {
  currentVendorAPI,
  currentVendorOptionsAPI,
  currentClientAPI,
  currentClientOptionsAPI
} from "@/services/api";
import debounce from "lodash/debounce";
import store from "@/store";

export default {
  name: "NavNar",
  mixins: [hasCurrentUser],
  props: {
    logoOnly: {
      type: Boolean,
      default: false
    }
  },
  created() {
    get(currentVendorOptionsAPI.getUrl("")).then(
      dto => {
        this.potentialVendors = dto.data.options;
        let matchingVendors = this.potentialVendors.filter(
          v => v.id === this.currentUser.vendorId
        );
        this.selectedVendor =
          matchingVendors.length > 0 ? matchingVendors[0] : null;
        this.vendorSelectorLoading = false;
      },
      err => {
        console.error(err);
      }
    );
    get(currentClientOptionsAPI.getUrl("")).then(
      dto => {
        this.potentialClients = dto.data.options;
        let matchingClients = this.potentialClients.filter(
          v => v.id === this.currentUser.clientId
        );
        this.selectedClient =
          matchingClients.length > 0 ? matchingClients[0] : null;
        this.clientSelectorLoading = false;
      },
      err => {
        console.error(err);
      }
    );
  },
  data: () => ({
    accountOpen: false,
    accountHover: false,
    selectedVendor: null,
    potentialVendors: [],
    vendorSelectorLoading: false,
    vendorSearchText: null,
    selectedClient: null,
    potentialClients: [],
    clientSelectorLoading: false,
    clientSearchText: null,
    assumedRole: "vendor-user"
  }),
  methods: {
    assumedRoleChanged(val) {
      if (val !== this.isAssumingClientRole) {
        if (val) store.dispatch("user/assumeClientRole");
        else store.dispatch("user/assumeVendorRole");
      }
    },
    useClientRole() {
      store.dispatch("user/assumeClientRole");
      this.assumedRole = "client-user";
    },
    useVendorRole() {
      store.dispatch("user/assumeVendorRole");
      this.assumedRole = "vendor-user";
    },
    bindLink(item) {
      if (item.href) return { href: item.href };
      if (item.path) return { to: item.path };
    }
  },
  computed: {
    navBarItems() {
      return getNavBarItems().filter(
        item =>
          (item.featureFlaggedOnBy === undefined ||
            this.isFeatureEnabled(item.featureFlaggedOnBy)) &&
          (item.featureFlaggedOffBy === undefined ||
            !this.isFeatureEnabled(item.featureFlaggedOffBy))
      );
    }
  },
  watch: {
    selectedVendor(val) {
      if (val.id === this.currentUser.vendorId) return;
      put(currentVendorAPI, { vendorId: val.id }).then(
        () => {
          store.dispatch("user/loadCurrentUser");
          this.selectedVendor = val;
          this.vendorSelectorLoading = false;
          this.$router.go();
        },
        err => {
          console.error(err);
        }
      );
    },
    selectedClient(val) {
      if (val.id === this.currentUser.clientId) return;
      put(currentClientAPI, { clientId: val.id }).then(
        () => {
          store.dispatch("user/loadCurrentUser");
          this.selectedClient = val;
          this.clientSelectorLoading = false;
          this.$router.go();
        },
        err => {
          console.error(err);
        }
      );
    },
    vendorSearchText: debounce(function(val) {
      this.vendorSelectorLoading = true;

      get(currentVendorOptionsAPI.getUrl({ text: val ? val : "" })).then(
        dto => {
          this.potentialVendors = dto.data.options;
          this.vendorSelectorLoading = false;
        },
        err => {
          console.error(err);
        }
      );
    }, 300),
    clientSearchText: debounce(function(val) {
      this.clientSelectorLoading = true;

      get(currentClientOptionsAPI.getUrl({ text: val ? val : "" })).then(
        dto => {
          this.potentialClients = dto.data.options;
          this.clientSelectorLoading = false;
        },
        err => {
          console.error(err);
        }
      );
    }, 300)
  }
};
</script>

<style lang="sass" scoped>
.v-app-bar
  .nav-logo
    margin-left:14px
    margin-right:24px
    margin-top:-3px
  .v-btn
    font-weight: 600
    font-size: 1rem
    &--active
      border-bottom: 2px solid white
.v-icon.active
  transform: rotate(180deg)
::v-deep.v-menu__content
  border-radius: 0
  .v-list
    border-radius: 0

.selector
  min-width: 250px
</style>
